import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, PaletteIcon, PenToolIcon, MessageSquareTextIcon, GlobeIcon, ScissorsIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
const courses = [{
  icon: BookOpenIcon,
  title: 'Arabic Language: Everyday Communication & Quranic Understanding',
  body: 'Focus on comprehension — from everyday Arabic to understanding the Quran you recite. For tajweed and memorization, we are glad to point you to dedicated Quran schools.'
}, {
  icon: MessageSquareTextIcon,
  title: 'English for Confidence & Public Life',
  body: 'For clear communication in work and daily life. Practical speaking and confidence building.'
}, {
  icon: GlobeIcon,
  title: 'European Languages (Integration Focus)',
  body: 'French / German / Spanish tracks designed for everyday integration and social confidence.'
}, {
  icon: PenToolIcon,
  title: 'Arabic Calligraphy',
  body: 'Culture meets creativity. A calm, structured craft with cultural depth and aesthetic discipline, including proper adab of sacred calligraphy.'
}, {
  icon: PaletteIcon,
  title: 'Islamic Arts & Crafts',
  body: 'Calligraphy, ornament, and traditional patterns — creative practice with cultural literacy, including proper adab of sacred calligraphy.'
}, {
  icon: ScissorsIcon,
  title: 'Modest Fashion & Clothing Design',
  body: 'Design and sewing of modest clothing. Includes practical skills with potential business application.'
}];
// Prices are temporarily hidden. Set to true to restore them.
const SHOW_PRICES = false;
export function CreativeLearning() {
  return <>
      <SubpageHero eyebrow="Language, culture & skills — heritage as strength" title="Creative Learning" paragraphs={['Creative Learning includes language, culture, and practical skills for Muslim adults living in non-Muslim countries.', 'These courses are designed for confidence, integration, and self-expression — enabling active social participation and skill acquisition while fully maintaining your heritage.']} primaryCta={{
      label: 'Explore Catalog',
      to: '#catalog'
    }} secondaryCta={{
      label: 'Request Info',
      to: '/en/contact'
    }} image="https://cdn.magicpatterns.com/uploads/ninMqHXNBrWb99GzWxD16c/Creative2.jpg" imageAlt="Muslim woman sketching a garment pattern at her desk beside Islamic arts books, calligraphy pens, and a laptop showing geometric designs" compact masked data-id="element-1979" data-mp="14sab3agyxxl9" />

      {/* Language, Culture & Skills */}
      <section id="catalog" className="py-16 md:py-24 scroll-mt-24 bg-sage-50/60" data-id="element-1980" data-mp="fbzn7y72py3c">
        <Container data-id="element-1981" data-mp="7tjkz71fuqwy">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-1982" data-mp="2v8e956fwwiy">
            Language, Culture & Skills
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-1983" data-mp="3u0hx8cw0oaj2">
            This direction combines cultural literacy, creativity, and everyday
            skills. It helps adults feel grounded, confident, and capable — in
            family life, public life, and community life.
          </p>

          <div className="mt-10 grid md:grid-cols-2 gap-5" data-id="element-1984" data-mp="3g9uvebuym4ir">
            {courses.map((c, i) => <motion.div key={c.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} className={`rounded-2xl border border-ink-900/5 p-6 shadow-soft ${['bg-sage-100', 'bg-[#F7EDE9]', 'bg-sand-100', 'bg-[#EDF2F7]', 'bg-sage-100', 'bg-[#F7EDE9]'][i]}`} data-id="element-1985" data-mp="3ba8n8gtznegt" data-mpi={i}>
                <span className="w-11 h-11 inline-flex items-center justify-center rounded-xl bg-white border border-forest-700/10 text-forest-700" data-id="element-1986" data-mp="3haocxcnr34o0">
                  <c.icon size={18} strokeWidth={1.6} data-id="element-1987" data-mp="33a3z6uwozz1d" />
                </span>
                <h3 className="mt-5 font-display text-lg text-ink-900" data-id="element-1988" data-mp="38bb3ul3jgi3t">
                  {c.title}
                </h3>
                <p className="mt-3 text-ink-700 text-sm leading-relaxed" data-id="element-1989" data-mp="2ucjvzgf9rso4">
                  {c.body}
                </p>
              </motion.div>)}
          </div>

          <div className="mt-8 flex flex-col gap-2 rounded-2xl border border-sage-200 bg-white px-6 py-5 sm:flex-row sm:items-center" data-id="element-1990" data-mp="skovzy2dlclq">
            <span className="inline-flex w-fit whitespace-nowrap rounded-full bg-sage-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700" data-id="element-1991" data-mp="wiml70w5byp4">
              Coming soon
            </span>
            <p className="flex-1 text-center text-sm font-semibold text-ink-700" data-id="element-1992" data-mp="2qsjd8j6ibbqe">
              The names of our confirmed teachers, educators, and Shariah Board
              members will be announced soon.
            </p>
          </div>
        </Container>
      </section>

      {/* Learning Formats */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-1993" data-mp="2vs5z1h0ei0at">
        <PatternDecor side="left" y="top" size="lg" data-id="element-1994" data-mp="39rp0jqypide5" />
        <Container data-id="element-1995" data-mp="39r5epz5xr4uu">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-1996" data-mp="39pktx19fb0a6">
            {SHOW_PRICES ? 'Learning Formats & Pricing' : 'Learning Formats'}
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-1997" data-mp="1fe628ewuqqrs">
            {[{
            title: 'Language Mastery',
            items: ['Programs: Arabic Language: Everyday Communication & Quranic Understanding, English for Confidence & Public Life, European Languages (Integration Focus)', 'Intensity: 2 live sessions per week', 'Format: Continuous training', 'Price: €95 / month']
          }, {
            title: 'Traditional Arts',
            items: ['Programs: Arabic Calligraphy, Islamic Arts & Crafts', 'Intensity: 1 live session per week', 'Format: Continuous training', 'Price: €60 / month']
          }, {
            title: 'Applied Design',
            items: ['Program: Modest Fashion & Clothing Design', 'Intensity: 1 live session per week', 'Format: Continuous training', 'Price: €65 / month']
          }].map((format, i) => <motion.div key={format.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} className={`rounded-2xl border border-ink-900/5 p-6 shadow-soft ${['bg-sage-100', 'bg-[#F7EDE9]', 'bg-sand-100'][i]}`} data-id="element-1998" data-mp="25ft79m6pwxed" data-mpi={i}>
                <h3 className="font-display text-lg text-ink-900" data-id="element-1999" data-mp="2ahsbeyaa7ycd">
                  {format.title}
                </h3>
                <ul className="mt-4 space-y-2.5" data-id="element-2000" data-mp="37x64edqxj2ns">
                  {format.items.filter(item => SHOW_PRICES || !item.startsWith('Price:')).map(item => <li key={item} className="flex items-start gap-2.5 text-ink-700 text-sm leading-relaxed" data-id="element-2001" data-mp="2bhbepcr7kppn">
                      <span className="w-1.5 h-1.5 rounded-full bg-forest-700 mt-2 flex-shrink-0" data-id="element-2002" data-mp="21kot2lr25umk" />
                      <span data-id="element-2003" data-mp="26i8jsl4xzzve">{item}</span>
                    </li>)}
                </ul>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* Why this matters */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-[#EDF2F7]/60" data-id="element-2004" data-mp="2z75nnj8epu4m">
        <PatternDecor side="right" y="middle" size="md" data-id="element-2005" data-mp="2l74n64w20opo" />
        <Container data-id="element-2006" data-mp="2p980z06745e4">
          <div className="grid lg:grid-cols-12 gap-12 items-center" data-id="element-2007" data-mp="2p8q4c2fmfp5o">
            <div className="lg:col-span-6" data-id="element-2008" data-mp="19sr6bjzpx9j5">
              <h2 className="font-display text-3xl md:text-4xl text-ink-900 leading-tight" data-id="element-2009" data-mp="19ry5t0rcrg9g">
                Build confidence through culture
              </h2>
              <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-2010" data-mp="16ajve5db1iv0">
                Creative learning goes beyond skill-building. It helps Muslim
                adults engage with their traditions in a way that strengthens
                daily life — at home, at work, and in the community.
              </p>
              <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-2011" data-mp="1b9db84g2dlyc">
                Every course is practical and grounded. You gain tools you will actually use.
              </p>
            </div>
            <div className="lg:col-span-6" data-id="element-2012" data-mp="ilhcku6xpjyx">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-card" data-id="element-2013" data-mp="imskn67exdvu">
                <img src="https://cdn.magicpatterns.com/uploads/1SkvqfxozFzGVZAoCy8He8/Arts2.jpg" alt="Muslim woman practising Islamic calligraphy with ink and a brush at a desk beside a laptop and art books" className="w-full h-full object-cover" data-id="element-2014" data-mp="xax4idi7bvg7" />
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Community Support & Access */}
      <section className="py-16 md:py-24 bg-sage-50/60" data-id="element-2015" data-mp="1c2be75z4j6rz">
        <Container data-id="element-2016" data-mp="184do80xj4gnj">
          <h3 className="font-display text-lg text-ink-900" data-id="element-2017" data-mp="180y9x0bojbn6">
            Community Support & Access
          </h3>
          <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-2018" data-mp="135sqq99nwb2s">
            Cultural and creative programs promote heritage appreciation,
            self-expression, and community skills, contributing to social
            inclusion and well-being. Some cohorts may include limited
            scholarship places through community partnerships.
          </p>
        </Container>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-2019" data-mp="fedlo8qi3fnu">
        <PatternDecor side="left" y="bottom" size="md" data-id="element-2020" data-mp="fckbijgoidb3" />
        <Container data-id="element-2021" data-mp="adzawixilrm4">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-2022" data-mp="aeiq3rvs61wg">
            <div className="max-w-xl" data-id="element-2023" data-mp="5gzfd1yak799">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-2024" data-mp="3rhx1bcyrrayy">
                Start learning in a creative cohort
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-2025" data-mp="9iwltzooikf">
                Request the upcoming cohort list and choose a course that fits
                your goals and schedule.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-2026" data-mp="a8ruuz1l0jcl">
              <Link to="/en/waitlist" data-id="element-2027" data-mp="38ryj4419c1hl">
                <Button variant="primary" data-id="element-2028" data-mp="38t07193dgigq">Join</Button>
              </Link>
              <Link to="/en/contact" data-id="element-2029" data-mp="3drbcz8qjq8to">
                <Button variant="secondary" data-id="element-2030" data-mp="3do5n3ra3f2ww">Request Info</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>;
}